/**
 * DataGapsTabPanel — the unified per-section "Issues" surface for the Variant C
 * inspector. Formerly split across a separate "Review" tab; the regulatory audit
 * and the missing-info (gap) checks now live together here.
 *
 * Ledger-backed (GET /runs/{runId}/sections/{moduleId}/issues): each row is one
 * persistent ``SectionIssueDoc`` with a lifecycle — open / resolved / dismissed /
 * stale — so the panel shows not just what's missing but what was already resolved
 * and HOW. Both checks feed the SAME ledger (``kind`` = "data_gap" | "regulatory"
 * | "language"); ``IssueRow`` renders regulatory findings inline (title + "To
 * resolve" guidance), so there is no separate regulatory display.
 *
 * Two checks, two triggers:
 *   - Missing-info (gap) check — runs on OPEN. The panel reads the section's live
 *     Word text and reconciles the ledger against it (``…/issues/reconcile``), so
 *     markers the writer deleted are auto-resolved-by-writer. No button.
 *   - Regulatory audit — the FDA/ICH LLM pass. Runs only from the "Run review"
 *     button here (or automatically on the backend right after a section drafts).
 *     Button = both checks: it runs the audit, then re-reconciles the gap ledger.
 *
 * Writer affordances per row (via IssueRow):
 *   click        — select the marker in the LIVE Word document (open only)
 *   Mark N/A     — dismiss without touching the document (reopenable)
 *   Ask asthra   — seed a chat turn that resolves the marker (host-wired)
 *
 * Guard: the on-open reconcile is SKIPPED when the live read is blank/absent or
 * the live doc is still loading — a blank read would falsely resolve every flag.
 * In that case (and outside Word — web/guru) it just loads the ledger; the
 * server's own sweep still auto-resolves against the saved SSOT.
 *
 * Fallback: when the issues API is unreachable (pre-rollout server), the panel
 * degrades to the legacy client-side scan of the section draft
 * (``extractGapMarkers`` — kept exported for that path and its tests).
 */

import { Button, makeStyles, mergeClasses, Spinner } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
	getRunAuditLibrary,
	getSectionContent,
	getSectionIssues,
	patchSectionIssue,
	reconcileSectionIssues,
	type RunAuditLibrary,
	type SectionIssue,
	startRunAuditLibrary,
} from "../../../api/client";
import { AuditLibraryPanel, auditLibraryBlocksAudit } from "./AuditLibraryPanel";
import { ReviewPanel } from "../../../components/review-agent/ReviewPanel";
import { REVIEW_AGENT_ENABLED } from "../../../config/reviewAgent";
import { isLiveDocLoading } from "../../../services/liveDocLoadState";
import { navigateToSection, readSectionSubtree, selectTextMarker } from "../../../services/wordWriter";
import type { SpineSection } from "../../hooks/useVariantCRun";
import {
	getAuditLibraryBuild,
	getEntry,
	hydrateFromServer,
	reviewKey,
	startAudit,
	subscribe,
	subscribeAuditLibraryBuild,
} from "../../reviewStore";
import type { AgentActivityLine } from "../../services/agentActivityFeed";
import type { RunIdentity } from "../../sseAdapter";
import { matchFlagMarker, sanitizeDraftForWord } from "../../services/draftSanitizer";
import { fonts, inkColors } from "../../tokens";
import { IssueRow, prefixLabel, severityRank, STATUS_ORDER } from "./IssueRow";
import PersonaReviewIssues from "./PersonaReviewIssues";

interface DataGapsTabPanelProps {
	section: SpineSection;
	runId: string;
	/** Host-wired "resolve with asthra" — seeds a chat turn scoped to this
	 *  section with the marker text. Button hidden when absent. */
	onResolveWithAsthra?: (issue: SectionIssue) => void;
	/** Monotonic signal bumped when a highlighted-span edit is approved. A
	 *  change re-fetches the open-items list so it reflects the re-checked
	 *  section (the server reconciles the ledger inline on approve). */
	issuesRefreshSignal?: number;
	/** (trial, session) — gates the persona scorecards (review_agent sidecar),
	 *  which stay behind ``REVIEW_AGENT_ENABLED``. Null before the run reports it. */
	identity?: RunIdentity | null;
	/** Live per-section agent step lines (from the shell's agentActivityFeed) for
	 *  THIS section. Drives the expandable "full step feed" while an audit runs;
	 *  empty when no feed is active. */
	auditActivityLines?: readonly AgentActivityLine[];
}

interface SectionPayload {
	sections?: Array<{ draft_content?: string }>;
}

// flags.yaml markers whose ``eval_category`` is "gap" — i.e. genuine
// source-data gaps the writer must resolve. Mirror of the prefix list in
// draftSanitizer.ts ``REGISTERED_MARKER_PREFIXES``, minus the non-gap markers:
// [REGULATOR FLAG: (regulatory), [REVIEWER: (honest), the subtask sentinels +
// PLACEHOLDER (system), and [IMAGE DATA: (a figure, not a data gap). Longest
// first so "[MISSING TABLE" is matched before "[MISSING INFO:" etc.
const GAP_MARKER_PREFIXES = new Set<string>([
	"[NOT FOUND - NOT IN SOURCE]",
	"[NOT FOUND - RETRIEVAL FAILURE]",
	"[IB CONTENT NEEDED:",
	"[MISSING TABLE",
	"[MISSING INFO:",
	"[NOT FOUND",
	"[PENDING",
	"[ERROR:",
	"[FILL:",
]);

// Single-line bracket token: no nested brackets, no newlines. Matches the
// gap-marker grammar (every gap marker is emitted on its own line / inline as
// one bracket). arr[0]-style indexing never starts with a gap prefix, so it's
// filtered out below.
const BRACKET_RE = /\[[^[\]\n]*\]/g;

interface GapEntry {
	/** Full marker text incl. brackets — the needle for selectTextMarker. */
	text: string;
	/** Matched gap prefix, e.g. "[MISSING INFO:". */
	prefix: string;
	/** Human-facing type label, e.g. "MISSING INFO". */
	label: string;
	/** Body description after the colon (empty for closed markers). */
	description: string;
}

/** Scan a sanitized draft for gap markers, de-duplicating identical markers
 *  (selectTextMarker hits the first occurrence anyway). Legacy fallback path
 *  for servers without the issues ledger. */
export function extractGapMarkers(draft: string): GapEntry[] {
	const out: GapEntry[] = [];
	const seen = new Set<string>();
	for (const m of draft.matchAll(BRACKET_RE)) {
		const text = m[0];
		const prefix = matchFlagMarker(text);
		if (!prefix || !GAP_MARKER_PREFIXES.has(prefix)) continue;
		if (seen.has(text)) continue;
		seen.add(text);
		const colon = text.indexOf(":");
		const description = colon !== -1 ? text.slice(colon + 1, -1).trim() : "";
		out.push({ text, prefix, label: prefixLabel(prefix), description });
	}
	return out;
}

const useStyles = makeStyles({
	root: { padding: "10px 12px" },
	loading: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
	},
	empty: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
		fontStyle: "italic",
		padding: "8px 2px",
	},
	error: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: "#B71C1C",
		margin: "4px 0",
	},
	// Regulatory run affordance (moved here from the former Review tab).
	reviewHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "8px",
		marginBottom: "10px",
		paddingBottom: "8px",
		borderBottom: "1px solid #E3E8EF",
	},
	reviewTitle: {
		fontFamily: fonts.body,
		fontSize: "12px",
		fontWeight: 600,
		color: inkColors.ink2,
	},
	progress: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
		fontStyle: "italic",
		margin: "0 0 8px",
	},
	reviewError: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: "#B71C1C",
		margin: "0 0 8px",
	},
	evidenceSummary: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
		fontStyle: "italic",
		cursor: "pointer",
	},
	evidence: {
		fontFamily: fonts.body,
		fontSize: "10.5px",
		color: inkColors.ink4,
		whiteSpace: "pre-wrap",
		marginTop: "8px",
		maxHeight: "160px",
		overflowY: "auto",
	},
	stepFeedSummary: {
		fontFamily: fonts.body,
		fontSize: "10.5px",
		color: inkColors.ink4,
		cursor: "pointer",
		margin: "0 0 6px",
	},
	stepFeed: {
		display: "flex",
		flexDirection: "column",
		gap: "3px",
		maxHeight: "160px",
		overflowY: "auto",
		margin: "0 0 8px",
	},
	stepLine: {
		fontFamily: fonts.body,
		fontSize: "10.5px",
		color: inkColors.ink4,
		whiteSpace: "pre-wrap",
	},
	headerRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: "8px",
	},
	count: {
		fontFamily: fonts.body,
		fontSize: "10.5px",
		color: inkColors.ink4,
	},
	list: { display: "flex", flexDirection: "column", gap: "6px" },
	// Row styling for ledger rows now lives in IssueRow. These few keys remain
	// for the legacy client-side-scan fallback (GapEntry rows), which renders a
	// simpler open-only row without the shared component.
	row: {
		display: "block",
		width: "100%",
		textAlign: "left",
		borderRadius: "5px",
		padding: "8px 10px",
		fontFamily: fonts.body,
		border: "none",
	},
	rowOpen: {
		background: "#FFF4F4",
		borderLeft: "3px solid #B71C1C",
	},
	rowLabelBtn: {
		border: "none",
		background: "transparent",
		padding: 0,
		cursor: "pointer",
		textAlign: "left",
		flex: "1 1 auto",
		":focus-visible": { outline: "2px solid #B71C1C", outlineOffset: "1px" },
	},
	rowLabel: {
		fontSize: "9px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		color: "#B71C1C",
	},
	rowDesc: {
		fontSize: "11.5px",
		color: inkColors.ink2,
		lineHeight: 1.4,
		wordBreak: "break-word",
	},
});

export const DataGapsTabPanel: React.FC<DataGapsTabPanelProps> = ({
	section,
	runId,
	onResolveWithAsthra,
	issuesRefreshSignal,
	identity,
	auditActivityLines,
}) => {
	const styles = useStyles();
	// Ledger mode (issues !== null) vs legacy scan fallback (gaps).
	const [issues, setIssues] = useState<SectionIssue[] | null>(null);
	const [gaps, setGaps] = useState<GapEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const requestKeyRef = useRef<string>("");

	// Regulatory-audit state lives in the module-scope reviewStore (keyed by
	// runId:moduleId) so an in-flight audit survives the inspector closing /
	// section switches / a full add-in reload. This panel subscribes for the
	// running spinner + FDA-precedent evidence; the findings themselves render
	// as ledger IssueRows below.
	const storeKey = reviewKey(runId, section.id);
	const entry = useSyncExternalStore(
		useCallback((cb: () => void) => subscribe(storeKey, cb), [storeKey]),
		() => getEntry(storeKey),
	);
	const running = entry.runState === "running";

	const wasDrafted =
		section.status === "drafted" || section.status === "needs_approval" || section.status === "flagged";
	// An in-flight or completed regulatory audit is itself proof the section has
	// real content. Don't let a transient spine-status downgrade (the live-run
	// snapshot reconcile repainting the current module as "drafting") collapse the
	// inspector to the "not drafted yet" empty state and drop the running progress.
	const hasDraftContent = wasDrafted || running || entry.hasRun || entry.flagCount > 0;

	// Load the ledger AND run the fast gap check against the live document.
	// Reconcile (auto-resolve markers absent from the live text) only when we have
	// usable live text and the doc isn't mid-load — otherwise a blank read would
	// falsely resolve every flag, so we just read the ledger (the server sweep
	// still settles absent markers against the saved SSOT).
	const loadIssues = useCallback(async () => {
		if (!hasDraftContent) {
			setLoading(false);
			setError(null);
			setActionError(null);
			setIssues(null);
			setGaps([]);
			return;
		}
		const key = `${runId}:${section.id}`;
		requestKeyRef.current = key;
		setLoading(true);
		setError(null);
		setActionError(null);
		// Ledger first (with the on-open live-doc reconcile); degrade to the legacy
		// client-side scan when the issues API is unavailable (older server).
		try {
			let liveText: string | null = null;
			// Do NOT reconcile the live text while this section's regulatory audit
			// is running: ``startAudit`` strips the section's [REGULATOR FLAG:…]
			// markers up front for a clean slate and only re-projects the fresh
			// flags at completion (reviewStore ``reprojectSection`` before the
			// ``done`` flip). Reconciling the stripped doc in between (this effect
			// re-runs on the ``audit_complete`` issuesRefreshSignal bump) would
			// resolve every brand-new flag as ``writer_manual``. Read the ledger
			// read-only instead; the runState→"done" effect below reconciles once
			// the flags are back in the doc. Read runState fresh from the store so
			// the racy refresh sees the in-flight audit, not a stale closure.
			const auditRunning = getEntry(reviewKey(runId, section.id)).runState === "running";
			if (section.number && !isLiveDocLoading() && !auditRunning) {
				try {
					liveText = await readSectionSubtree(section.number);
				} catch {
					liveText = null;
				}
			}
			if (requestKeyRef.current !== key) return;
			const resp =
				liveText && liveText.trim()
					? await reconcileSectionIssues(runId, section.id, liveText)
					: await getSectionIssues(runId, section.id, "all");
			if (requestKeyRef.current !== key) return;
			setIssues(resp.issues);
			setLoading(false);
			return;
		} catch {
			// fall through to legacy scan
		}
		try {
			const resp = (await getSectionContent(runId, section.id)) as unknown as SectionPayload;
			if (requestKeyRef.current !== key) return;
			const draft = (resp.sections ?? [])
				.map((s) => sanitizeDraftForWord(s.draft_content ?? ""))
				.join("\n\n");
			setGaps(extractGapMarkers(draft));
		} catch (err) {
			if (requestKeyRef.current !== key) return;
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			if (requestKeyRef.current === key) setLoading(false);
		}
	}, [runId, section.id, section.number, hasDraftContent]);

	// Fresh check on open (and when the focused section or the external
	// issuesRefreshSignal changes). loadIssues re-identifies on section change,
	// so this covers navigation too.
	// biome-ignore lint/correctness/useExhaustiveDependencies: issuesRefreshSignal is an explicit re-run trigger
	useEffect(() => {
		void loadIssues();
	}, [loadIssues, issuesRefreshSignal]);

	// Restore any persisted regulatory findings / evidence into the store and
	// re-attach to a still-running audit — WITHOUT starting one. The audit runs
	// only from the "Run review" button (or the backend post-draft auto-audit).
	useEffect(() => {
		void hydrateFromServer(runId, section.id);
	}, [runId, section.id]);

	// When an audit finishes (owned, attached, or via SSE), its [REGULATOR FLAG:…]
	// findings are now persisted in the ledger — re-load so the new regulatory
	// rows appear (and the gap ledger is re-reconciled against the freshly
	// re-projected document).
	const prevRunStateRef = useRef(entry.runState);
	useEffect(() => {
		const prev = prevRunStateRef.current;
		prevRunStateRef.current = entry.runState;
		if (prev !== "done" && entry.runState === "done") {
			void loadIssues();
		}
	}, [entry.runState, loadIssues]);

	// Precedent-aware audit library: status + per-doc processing + evidence.
	// Fetched on open and refreshed when an audit completes; null when the
	// feature is off (the panel renders nothing in that case).
	const [auditLib, setAuditLib] = useState<RunAuditLibrary | null>(null);
	const loadAuditLibrary = useCallback(() => {
		void getRunAuditLibrary(runId)
			.then((data) => setAuditLib(data))
			.catch(() => setAuditLib(null));
	}, [runId]);

	// Live audit-library build phase (SSE-driven, run-scoped) — narrates the build
	// between the 6s status polls, and surfaces the custom-submission temp-library
	// bootstrap that the poll can miss entirely (no documents yet).
	const auditBuildPhase = useSyncExternalStore(subscribeAuditLibraryBuild, () => getAuditLibraryBuild(runId));
	const liveBuildMessage = auditBuildPhase?.message ?? null;
	useEffect(() => {
		loadAuditLibrary();
	}, [loadAuditLibrary]);

	// "Run review" = both checks. Fire-and-forget the regulatory audit (its every
	// transition lands in the store, so it completes even if this panel unmounts);
	// the run-state effect above re-loads the gap ledger once it's done.
	const runReview = useCallback(() => {
		// Custom submissions (synthetic ``CUSTOM-N`` module ids) carry the writer's
		// real heading number/title so the audit can (1) sync the live section into
		// the SSOT before running and (2) re-project its markers at the actual
		// heading — without which the markers never land and the on-open reconcile
		// auto-resolves every new finding. A numbered heading is required (the live
		// capture + reconcile both key off the number).
		const custom =
			section.id.startsWith("CUSTOM-") && section.number
				? { sectionNumber: section.number, title: section.title }
				: undefined;
		void startAudit(runId, section.id, custom);
	}, [runId, section.id, section.number, section.title]);

	// Keep the audit-library status fresh once an audit settles (docs may have
	// finished processing, evidence may have landed).
	useEffect(() => {
		if (entry.runState === "done") loadAuditLibrary();
	}, [entry.runState, loadAuditLibrary]);

	// Manual (re)build of the audit library, with an optional steering note.
	//
	// ``settingUpAudit`` is a latch that stays true for the WHOLE (re)build, not
	// just the POST. The POST returns 202 immediately, at which point the spine
	// ref still shows the prior terminal status (e.g. a reconciled ``failed``) —
	// clearing the latch there (the old bug) let ``auditPolling`` compute false and
	// polling stopped while the backend kept building, so the panel never advanced.
	// Instead we hold the latch until a poll observes the build reach a NEW terminal
	// state (``auditBuildSeen`` guards against the stale pre-build terminal), with a
	// hard timeout so a build that never starts can't poll forever.
	const [settingUpAudit, setSettingUpAudit] = useState(false);
	const [auditBuildSeen, setAuditBuildSeen] = useState(false);
	const [auditSetupError, setAuditSetupError] = useState<string | null>(null);
	const rebuildTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearRebuildTimer = useCallback(() => {
		if (rebuildTimer.current) {
			clearTimeout(rebuildTimer.current);
			rebuildTimer.current = null;
		}
	}, []);
	const handleSetupAuditLibrary = useCallback(
		(steering: string) => {
			setAuditSetupError(null);
			setAuditBuildSeen(false);
			setSettingUpAudit(true);
			clearRebuildTimer();
			// Safety net: a build that never flips to acquiring (feature off, no
			// token/library) shouldn't poll indefinitely.
			rebuildTimer.current = setTimeout(() => setSettingUpAudit(false), 5 * 60 * 1000);
			void startRunAuditLibrary(runId, steering)
				.then(() => loadAuditLibrary())
				.catch((err) => {
					setAuditSetupError(err instanceof Error ? err.message : String(err));
					setSettingUpAudit(false);
					clearRebuildTimer();
				});
		},
		[runId, loadAuditLibrary, clearRebuildTimer],
	);

	// While a build is acquiring/processing (or the rebuild latch is held), poll the
	// status so the panel advances on its own — the built-in Refresh is hidden on
	// the not-ready path.
	const auditStatus = auditLib?.audit_library?.status;
	// ``!!auditBuildPhase`` starts the poll the moment the first audit_library_*
	// SSE arrives (e.g. a "Run review"-triggered build the manual latch never set),
	// so ``auditLib`` catches up to acquiring/processing and the panel advances.
	const auditPolling =
		settingUpAudit || auditStatus === "acquiring" || auditStatus === "processing" || !!auditBuildPhase;
	useEffect(() => {
		if (!auditPolling) return;
		const t = setInterval(loadAuditLibrary, 6000);
		return () => clearInterval(t);
	}, [auditPolling, loadAuditLibrary]);

	// Release the rebuild latch once the build has actually started (seen
	// acquiring/processing) AND then reached a terminal state — so a stale terminal
	// present at click time never ends the latch early.
	useEffect(() => {
		if (!settingUpAudit) return;
		if (auditStatus === "acquiring" || auditStatus === "processing") {
			if (!auditBuildSeen) setAuditBuildSeen(true);
			return;
		}
		if (auditBuildSeen && (auditStatus === "ready" || auditStatus === "empty" || auditStatus === "failed")) {
			setSettingUpAudit(false);
			clearRebuildTimer();
		}
	}, [settingUpAudit, auditBuildSeen, auditStatus, clearRebuildTimer]);

	// Clean up the safety timer on unmount.
	useEffect(() => clearRebuildTimer, [clearRebuildTimer]);

	const auditBlocked = auditLibraryBlocksAudit(auditLib);

	// Jump to the marker in the live Word document; fall back to the section
	// heading when the exact marker text isn't found (doc not projected, or the
	// marker was edited out).
	const jumpToMarker = useCallback(
		(markerText: string) => {
			void selectTextMarker(markerText)
				.then((found) => {
					if (!found && section.number) {
						return navigateToSection(section.number);
					}
					return undefined;
				})
				.catch(() => {});
		},
		[section.number],
	);

	const handlePatch = useCallback(
		async (issue: SectionIssue, status: "dismissed" | "open") => {
			setActionError(null);
			try {
				const updated = await patchSectionIssue(runId, issue.id, status);
				setIssues((prev) => (prev ? prev.map((i) => (i.id === updated.id ? updated : i)) : prev));
			} catch (err) {
				setActionError(err instanceof Error ? err.message : String(err));
			}
		},
		[runId],
	);

	// The regulatory run affordance (header + progress + evidence) — shown for any
	// drafted section, independent of the ledger fetch state below.
	const reviewControls = hasDraftContent ? (
		<>
			<div className={styles.reviewHeader}>
				<span className={styles.reviewTitle}>
					Regulatory review (US FDA / ICH)
					{entry.flagCount > 0 ? ` · ${entry.flagCount}` : ""}
				</span>
				<Button
					size="small"
					appearance="primary"
					disabled={running || auditBlocked}
					title={auditBlocked ? "Audit library is still processing — available once all documents are ready" : undefined}
					onClick={runReview}
				>
					{running ? "Running…" : entry.hasRun || entry.flagCount > 0 ? "Re-run review" : "Run review"}
				</Button>
			</div>
			<AuditLibraryPanel
				data={auditLib}
				onRefresh={loadAuditLibrary}
				hideWhenNotReady
				allowSetup
				onSetup={handleSetupAuditLibrary}
				settingUp={settingUpAudit}
				setupError={auditSetupError ?? undefined}
				liveBuildMessage={liveBuildMessage}
			/>
			{running && (
				<>
					<div className={styles.progress}>
						<Spinner size="tiny" />
						<span>{entry.progress || section.subline || "Running regulatory review…"}</span>
					</div>
					{auditActivityLines && auditActivityLines.length > 0 ? (
						<details>
							<summary className={styles.stepFeedSummary}>
								Show steps ({auditActivityLines.length})
							</summary>
							<div className={styles.stepFeed}>
								{auditActivityLines.map((line) => (
									<span key={line.id} className={styles.stepLine}>
										{line.depth > 0 ? "  ".repeat(line.depth) : ""}
										{line.text}
									</span>
								))}
							</div>
						</details>
					) : null}
				</>
			)}
			{entry.error && <div className={styles.reviewError}>{entry.error}</div>}
			{entry.statusNote === "no_guidance" && entry.runState === "done" ? (
				<div className={styles.empty}>No FDA/ICH regulatory guidance is configured for this section yet.</div>
			) : null}
			{entry.evidence ? (
				<details>
					<summary className={styles.evidenceSummary}>FDA precedent evidence</summary>
					<div className={styles.evidence}>{entry.evidence}</div>
				</details>
			) : null}
		</>
	) : null;

	// Persona review issues (read-only) + the sidecar scorecards (behind the
	// kill switch) render beneath the ledger — moved here from the old Review tab.
	const personaSurfaces = hasDraftContent ? (
		<>
			<PersonaReviewIssues key={`${runId}:${section.id}`} runId={runId} moduleId={section.id} />
			{REVIEW_AGENT_ENABLED && identity ? (
				<ReviewPanel sessionId={`${identity.trial}__${identity.session}`} moduleId={section.id} />
			) : null}
		</>
	) : null;

	if (loading) {
		return (
			<div className={styles.root}>
				{reviewControls}
				<div className={styles.loading}>
					<Spinner size="extra-tiny" />
					<span>Scanning for issues…</span>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.root}>
				{reviewControls}
				<div className={styles.error}>Couldn&apos;t load this section&apos;s gaps: {error}</div>
			</div>
		);
	}

	if (!hasDraftContent) {
		return (
			<div className={styles.root}>
				<div className={styles.empty}>This section hasn&apos;t been drafted yet — no issues to show.</div>
			</div>
		);
	}

	// ── Legacy fallback rendering (no ledger on this server) ───────────────
	if (issues === null) {
		return (
			<div className={styles.root}>
				{reviewControls}
				{gaps.length === 0 ? (
					<div className={styles.empty}>No issues in this section. ✓</div>
				) : (
					<>
						<div className={styles.count}>
							{gaps.length} {gaps.length === 1 ? "issue" : "issues"} · click to jump to it in the document
						</div>
						<div className={styles.list}>
							{gaps.map((gap, i) => (
								<button
									type="button"
									key={`${gap.text}-${i}`}
									className={mergeClasses(styles.row, styles.rowOpen, styles.rowLabelBtn)}
									onClick={() => jumpToMarker(gap.text)}
									title="Select this marker in the Word document"
								>
									<div className={styles.rowLabel}>{gap.label}</div>
									<div className={styles.rowDesc}>{gap.description || gap.text}</div>
								</button>
							))}
						</div>
					</>
				)}
				{personaSurfaces}
			</div>
		);
	}

	// ── Ledger-backed rendering ─────────────────────────────────────────────
	// While a regulatory audit is running, present the section's clean slate:
	// the prior regulatory rows are being superseded server-side and their
	// live-doc markers (prose AND table cells) have already been stripped, so
	// hide them here too rather than show flags we've just removed. The fresh
	// set re-loads when the run reaches "done" (the run-state effect above).
	const visibleIssues = running ? issues.filter((i) => i.kind !== "regulatory") : issues;
	const sorted = [...visibleIssues].sort(
		(a, b) =>
			STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
			severityRank(a.severity) - severityRank(b.severity) ||
			(a.first_seen_at ?? "").localeCompare(b.first_seen_at ?? ""),
	);
	const openCount = visibleIssues.filter((i) => i.status === "open").length;

	return (
		<div className={styles.root}>
			{reviewControls}
			<div className={styles.headerRow}>
				<div className={styles.count}>
					{openCount} open {openCount === 1 ? "issue" : "issues"} · {visibleIssues.length} total
				</div>
			</div>
			{actionError ? <div className={styles.error}>{actionError}</div> : null}
			{sorted.length === 0 ? (
				<div className={styles.empty}>No data gaps in this section. ✓</div>
			) : (
				<div className={styles.list}>
					{sorted.map((issue) => (
						<IssueRow
							key={issue.id}
							issue={issue}
							onJump={(i) => jumpToMarker(i.marker_text)}
							onDismiss={(i) => void handlePatch(i, "dismissed")}
							onReopen={(i) => void handlePatch(i, "open")}
							onResolveWithAsthra={onResolveWithAsthra}
						/>
					))}
				</div>
			)}
			{personaSurfaces}
		</div>
	);
};

export default DataGapsTabPanel;
